import type { Configuration, Result } from './types';
export interface CategoryOption { id: string; name: string }
export interface CreationDraft { id: number; sku: string; name: string; categoryId: string }
const normalize = (value: string) => value.trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase();
const header = (value: unknown) => typeof value === 'string' ? normalize(value).replace(/[^a-z0-9]/g, '') : '';

export function validateCreationRows(rows: CreationDraft[], categories: CategoryOption[]): CreationDraft[] {
  if (!rows.length || rows.length > 5000) throw new Error('Ingresa entre 1 y 5000 modelos por lote.');
  const seen = new Set<string>();
  return rows.map((row, index) => {
    const sku = row.sku.trim(); const name = row.name.trim();
    if (!sku || /[\r\n]/.test(sku)) throw new Error(`Fila ${index + 1}: indica un SKU en una sola línea.`);
    if (seen.has(sku)) throw new Error(`Fila ${index + 1}: SKU repetido: ${sku}.`);
    seen.add(sku);
    if (!name || name.length > 500) throw new Error(`Fila ${index + 1}: indica un nombre de hasta 500 caracteres.`);
    if (!categories.some(category => category.id === row.categoryId)) throw new Error(`Fila ${index + 1}: selecciona una categoría del cliente.`);
    return { ...row, sku, name };
  });
}

export function creationFromSheet(sheet: unknown[][], categories: CategoryOption[]): CreationDraft[] {
  const headers = (sheet[0] ?? []).map(header);
  const columns = ['skudestino', 'nombre', 'categoria'].map(name => headers.indexOf(name));
  if (columns.some(column => column < 0)) throw new Error('La primera fila debe incluir SKU destino, Nombre y Categoría.');
  const categoriesByName = new Map<string, CategoryOption[]>();
  for (const category of categories) {
    const key = normalize(category.name);
    categoriesByName.set(key, [...(categoriesByName.get(key) ?? []), category]);
  }
  const rows: CreationDraft[] = [];
  sheet.slice(1).forEach((row, index) => {
    const values = columns.map(column => row[column]);
    if (values.every(value => value == null || (typeof value === 'string' && !value.trim()))) return;
    if (values.some(value => typeof value !== 'string' || !value.trim())) {
      throw new Error(`Fila ${index + 2}: SKU, Nombre y Categoría deben contener texto. Formatea SKU destino como Texto para conservar ceros y signos.`);
    }
    const [sku, name, categoryName] = values as string[];
    const matches = categoriesByName.get(normalize(categoryName)) ?? [];
    if (!matches.length) throw new Error(`Fila ${index + 2}: no existe la categoría «${categoryName.trim()}» para el cliente seleccionado.`);
    if (matches.length > 1) throw new Error(`Fila ${index + 2}: la categoría «${categoryName.trim()}» es ambigua. Hay varias categorías con ese nombre para el cliente; usa la selección manual.`);
    rows.push({ id: index, sku, name, categoryId: matches[0].id });
  });
  return validateCreationRows(rows, categories);
}

// Keep each row's metadata throughout preview, explicit target selection and execution.
export function configurationForRow(config: Configuration, row: Pick<Result, 'creation'>): Configuration {
  return { ...config, createModel: row.creation };
}
